/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { PayloadAction } from '@reduxjs/toolkit';
import { isArray } from 'lodash';
import { EventChannel, Task } from 'redux-saga';
import { all, cancel, delay, fork, put, race, take } from 'redux-saga/effects';

import { getFirebaseAnalytics } from '~/apis/firebase';
import { config, directionalGameLog, gameLogger } from '~/app';
import { setConnected, setCurrentState } from '~/game';
import { setEventLog } from '~/overlay';
import { TournamentOverview } from '~/server/src/dtos';
import { Horse, JoinTournamentRequest, Player } from '~/server/src/dtos/controller';
import { Controller, View } from '~/server/src/interfaces';
import Turfmaster from '~/server/src/tmmodel';

import {
  joinTournament,
  joinTournamentFailed,
  JoinTournamentParameters,
  refreshTournaments,
} from '../actions';
import { SetupChannelEvent } from '../channelEvent';
import { setLastActiveGameId } from '../reconnectStorage';
import { actions } from '../state';
import { takeUntilPreserving } from '../utils/saga';

/** Tournament join: errors arrive as JOINTOURNAMENT with params.error; success is JOINGAME (respondJoinGame), not a JOINTOURNAMENT ack. */
function isJoinTournamentHandshakeResolved(event: SetupChannelEvent): boolean {
  if (event.state === Turfmaster.stateMachine.JOINTOURNAMENT && event.params?.error) {
    return true;
  }
  if (event.state === Turfmaster.stateMachine.JOINGAME) {
    return true;
  }
  const eventName = Object.keys(Turfmaster.stateMachine)[event.state] ?? String(event.state);
  directionalGameLog.inbound(eventName, { phase: 'ignored' }, 'controller event (ignored)');
  gameLogger.debug(
    { state: Object.keys(Turfmaster.stateMachine)[event.state], params: event.params },
    'controller => client (ignored while waiting for join tournament response)',
  );
  return false;
}

export function* sagaSetupTournamentPullGames(
  view: View,
  controller: Controller,
  channel: EventChannel<SetupChannelEvent>,
) {
  try {
    while (true) {
      yield put(actions.setIsRefreshingTournaments(true));
      try {
        controller.receiveMsg(Turfmaster.stateMachine.GETTOURNAMENTS, {
          view,
        });
        const getTournamentsResult = (yield* takeUntilPreserving(channel, (event) => {
          if (event.state === Turfmaster.stateMachine.GETTOURNAMENTS) return true;
          const eventName =
            Object.keys(Turfmaster.stateMachine)[event.state] ?? String(event.state);
          directionalGameLog.inbound(eventName, { phase: 'ignored' }, 'controller event (ignored)');
          gameLogger.debug(
            { state: Object.keys(Turfmaster.stateMachine)[event.state], params: event.params },
            'controller => client (ignored while waiting for get tournaments response)',
          );
          return false;
        })) as { matched: SetupChannelEvent; buffered: SetupChannelEvent[] };
        const { params } = getTournamentsResult.matched;
        directionalGameLog.inbound(
          'GETTOURNAMENTS',
          { phase: 'response', payloadSummary: { params } },
          'controller event',
        );
        const tournaments = (
          isArray(params.tournaments) ? params.tournaments : []
        ) as TournamentOverview[];
        yield put(actions.setTournaments(tournaments));
      } finally {
        yield put(actions.setIsRefreshingTournaments(false));
      }
      // Wait for the next polling tick, but trigger immediately when the user requests a refresh.
      yield race({
        timeout: delay(config.tournamentPullInterval),
        refresh: take(refreshTournaments.type),
      });
    }
  } finally {
    // Clean up any resources if needed
    gameLogger.debug('Pull tournaments saga cancelled');
  }
}

function* sagaSetupTournament(
  view: View,
  controller: Controller,
  channel: EventChannel<SetupChannelEvent>,
) {
  if (!view || !controller || !channel) {
    gameLogger.warn(
      'sagaSetupTournament exited because view, controller or channel is not defined',
    );
    return { ok: false, replayEvents: [] as SetupChannelEvent[] };
  }

  gameLogger.info('sagaSetupTournament started');

  const analytics = getFirebaseAnalytics();
  let replayEvents: SetupChannelEvent[];

  // fork a separate process constantly pulling tournaments to join
  const pullGamesTask: Task = yield fork(sagaSetupTournamentPullGames, view, controller, channel);

  try {
    // wait for a redux action requesting game creation or join
    const joinTournamentRequest: PayloadAction<JoinTournamentParameters> = yield take(
      joinTournament.type,
    );

    yield cancel(pullGamesTask);

    const { tournament, gameType, horseSelection } = joinTournamentRequest.payload;
    const player: Player = {
      horses: horseSelection.map<Horse>(({ horseId }) => ({
        horseid: horseId,
      })),
    };

    const joinTournamentParams: JoinTournamentRequest = {
      tournamentid: tournament.tournamentId,
      player,
      view,
    };
    directionalGameLog.outbound(
      'JOINTOURNAMENT',
      {
        phase: 'request',
        ids: { tournamentId: joinTournamentParams.tournamentid },
        payloadSummary: { horseIds: player.horses?.map((h) => h.horseid) ?? [] },
      },
      'controller event',
    );
    controller.receiveMsg(Turfmaster.stateMachine.JOINTOURNAMENT, joinTournamentParams);

    const joinHandshake = (yield* takeUntilPreserving(
      channel,
      isJoinTournamentHandshakeResolved,
    )) as { matched: SetupChannelEvent; buffered: SetupChannelEvent[] };
    replayEvents = joinHandshake.buffered;
    const matched = joinHandshake.matched;

    if (matched.state === Turfmaster.stateMachine.JOINTOURNAMENT && matched.params?.error) {
      yield put(setCurrentState(matched.state));
      yield put(joinTournamentFailed({ error: matched.params.error }));
      return { ok: false, replayEvents: [] };
    }

    if (matched.state !== Turfmaster.stateMachine.JOINGAME) {
      gameLogger.warn(
        { state: matched.state, stateName: Object.keys(Turfmaster.stateMachine)[matched.state] },
        'unexpected join tournament handshake event',
      );
      return { ok: false, replayEvents: replayEvents! };
    }

    const joinGameResponse = matched;
    yield put(setCurrentState(joinGameResponse.state));

    directionalGameLog.inbound(
      'JOINGAME',
      {
        phase: 'response',
        ids: { gameId: joinGameResponse.params.gameid, playerId: joinGameResponse.params.playerid },
        payloadSummary: { params: joinGameResponse.params },
      },
      'controller event',
    );

    const gameId = joinGameResponse.params.gameid;
    const game = controller.getGame();
    const board = controller.getBoard();
    const eventLog = controller.getEventLog();

    // make sure join was successful and game / board are valid
    if (!joinGameResponse.params.error && game && board) {
      const nowTime = new Date().getTime();
      const nextStartDate = tournament.startingTimes.find((date) => date.getTime() > nowTime);

      yield all([
        put(actions.setGameId(gameId)),
        put(actions.setGameType(gameType)),
        ...(nextStartDate ? [put(actions.setGameStartDate(nextStartDate))] : []),
        put(actions.setPlayerId(joinGameResponse.params.playerid)),
        put(setEventLog(eventLog)),
        put(setConnected(true)),
      ]);
      setLastActiveGameId(gameId);

      // make sure game and board are valid
      if (!controller.getGame() || !controller.getBoard()) {
        gameLogger.warn(
          'sagaSetup exit join after game creation because game or board is not defined',
        );
        return { ok: false, replayEvents: replayEvents! };
      }

      analytics?.logEvent('join_game', {
        connectionType: controller.getType(),
        gameType,
        playerCount: config.tournamentPlayerCount,
        horseCountPerPlayer: config.tournamentPlayerCount,
      });

      // everything worked so notify mainSaga that game started successfully
      return { ok: true, replayEvents: replayEvents! };
    }

    return { ok: false, replayEvents: [] };
  } finally {
    // make sure pullGamesTask cancellation is guaranteed even if this saga is interrupted
    yield cancel(pullGamesTask);
  }
}

export default sagaSetupTournament;
